import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closePanel, setSelectedClass } from '../../store/slices/calendarSlice';
import { createClass, updateClass, deleteClass, addClassLocal, updateClassLocal, fetchClasses } from '../../store/slices/classesSlice';
import { findStudentsWithInvalidParentEmails } from '../../utils/parentEmails';
import { moveStudent } from '../../services/api';
import { CLASS_TYPES, AGE_GROUPS, DURATION_OPTIONS, BHANGRA_INSTRUCTORS, GIDHA_INSTRUCTORS, DHOL_INSTRUCTORS, BOLLYWOOD_INSTRUCTORS, LOCATIONS, RUMAAL_RANKS } from '../../utils/constants';
import { timeSlots, formatTime, getAllTimeOptions } from '../../utils/timeUtils';
import StudentList from '../student-list/StudentList';
import InstructorList from '../instructor-list/InstructorList';
import LocationList from '../location-list/LocationList';
import SearchableSelect from '../common/SearchableSelect';
import { calculateClassRumaalRank } from '../../utils/rumaal';

const ClassDetailsPanel = () => {
  const dispatch = useDispatch();
  const { selectedClass, isPanelOpen, highlightedStudent } = useSelector((state) => state.calendar);
  const { classes } = useSelector((state) => state.classes);
  const { instructors } = useSelector((state) => state.instructors);
  const { locations } = useSelector((state) => state.locations);

  const [isEditing, setIsEditing] = useState(false);
  const [showInstructorList, setShowInstructorList] = useState(false);
  const [showLocationList, setShowLocationList] = useState(false);
  // Move-student-to-another-class flow
  const [movingStudent, setMovingStudent] = useState(null); // { id, name } | null
  const [moveTargetId, setMoveTargetId] = useState('');
  const [isMoving, setIsMoving] = useState(false);
  const [moveError, setMoveError] = useState('');
  // Draft to restore after a post-move fetchClasses() — the refresh replaces
  // selectedClass, which would otherwise wipe unsaved edits and exit edit mode.
  const pendingDraftRef = useRef(null);
  const [formData, setFormData] = useState({
    type: 'Bhangra',
    ageGroup: 'Teens',
    instructor: '',
    day: 'Monday',
    startTime: '14:00',
    duration: 60,
    location: '',
    notes: '',
    rumaalLevel: 'White',
    name: '',
    students: [],
  });

  // Sync selectedClass with the latest class data from Redux store
  useEffect(() => {
    if (selectedClass) {
      const classId = selectedClass.id || selectedClass._id;
      if (classId) {
        // Find the latest class data from Redux store
        const latestClass = classes.find(
          (c) => c.id === classId || c._id === classId
        );
        if (latestClass && latestClass !== selectedClass) {
          // Update selectedClass if it's different from the store
          dispatch(setSelectedClass(latestClass));
        }
      }
    }
  }, [classes, selectedClass, dispatch]);

  useEffect(() => {
    if (selectedClass) {
      const pending = pendingDraftRef.current;
      if (pending) {
        pendingDraftRef.current = null;
        // Only restore the draft onto the same class it came from.
        if (pending.classId === (selectedClass.id || selectedClass._id)) {
          setFormData(pending.draft);
          setIsEditing(true);
          return;
        }
      }
      const students = selectedClass.students || [];
      // Auto-calculate rumaal level from students
      const calculatedRumaalLevel = calculateClassRumaalRank(students);
      
      setIsEditing(selectedClass.isNew || false);
      setFormData({
        type: selectedClass.type || 'Bhangra',
        ageGroup: selectedClass.ageGroup || 'Teens',
        instructor: selectedClass.instructor || '',
        day: selectedClass.day || 'Monday',
        startTime: selectedClass.startTime || '14:00',
        duration: selectedClass.duration || 60,
        location: selectedClass.location || '',
        notes: selectedClass.notes || '',
        rumaalLevel: calculatedRumaalLevel, // Use calculated value, not stored value
        name: selectedClass.name || '',
        students: students,
      });
    }
  }, [selectedClass]);

  if (!isPanelOpen || !selectedClass) {
    return null;
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      
      // If class type changes, reset instructor if it's not valid for the new type
      if (field === 'type') {
        const validInstructors = getInstructorsForType(value);
        if (prev.instructor && !validInstructors.includes(prev.instructor)) {
          updated.instructor = '';
        }
      }
      
      // Auto-calculate rumaal level when students change
      if (field === 'students') {
        updated.rumaalLevel = calculateClassRumaalRank(value);
      }
      
      return updated;
    });
  };

  const handleSave = async () => {
    // The save below updates the store optimistically and closes the panel, so a
    // rejected write would leave a bad address on screen with nothing to say it
    // never saved. Catch it here instead, naming the student to fix.
    const badEmails = findStudentsWithInvalidParentEmails(formData.students || []);
    if (badEmails.length) {
      const detail = badEmails
        .map(({ name, invalid }) => `${name}: ${invalid.join(', ')}`)
        .join('\n');
      alert(`Please fix these parent email addresses before saving:\n\n${detail}`);
      return;
    }

    // Auto-calculate rumaal level from students before saving
    const calculatedRumaalLevel = calculateClassRumaalRank(formData.students || []);

    const classData = {
      ...selectedClass,
      ...formData,
      students: formData.students || [], // Use students from formData
      rumaalLevel: calculatedRumaalLevel, // Always use calculated value
    };

    if (selectedClass.isNew) {
      // Optimistic update
      const tempId = `class-${Date.now()}`;
      dispatch(addClassLocal({ ...classData, id: tempId }));
      // Persist to backend
      await dispatch(createClass(classData));
    } else {
      const classId = selectedClass.id || selectedClass._id;
      // Optimistic update
      dispatch(updateClassLocal({ ...classData, id: classId }));
      // Persist to backend
      if (classId) {
        await dispatch(updateClass({ id: classId, data: classData }));
      }
    }

    setIsEditing(false);
    dispatch(closePanel());
  };

  const handleStudentsChange = (updatedStudents) => {
    // Auto-calculate rumaal level when students change
    const calculatedRumaalLevel = calculateClassRumaalRank(updatedStudents);
    setFormData((prev) => ({ 
      ...prev, 
      students: updatedStudents,
      rumaalLevel: calculatedRumaalLevel,
    }));
  };

  // Open the "move student" picker for a given student.
  const handleMoveStudent = (studentId, studentName) => {
    setMoveError('');
    setMoveTargetId('');
    setMovingStudent({ id: studentId, name: studentName });
  };

  const cancelMove = () => {
    setMovingStudent(null);
    setMoveTargetId('');
    setMoveError('');
  };

  // Readable label for a class in the move picker.
  const getClassLabel = (cls) => {
    const base = cls.name || `${cls.type} – ${cls.ageGroup}`;
    const when = [cls.day, cls.startTime && formatTime(cls.startTime)]
      .filter(Boolean)
      .join(' ');
    return when ? `${base} (${when})` : base;
  };

  const confirmMove = async () => {
    if (!moveTargetId || !movingStudent) return;
    const sourceClassId = selectedClass.id || selectedClass._id;
    setIsMoving(true);
    setMoveError('');
    try {
      await moveStudent(movingStudent.id, sourceClassId, moveTargetId);
    } catch (err) {
      setMoveError(
        err?.response?.data?.error || 'Could not move the student. Please try again.'
      );
      setIsMoving(false);
      return;
    }
    // Move succeeded — drop the student locally and stash the draft so the
    // refresh below doesn't wipe unsaved edits or kick the user out of edit mode.
    const remaining = (formData.students || []).filter(
      (s) => (s._id || s.id) !== movingStudent.id
    );
    handleStudentsChange(remaining);
    pendingDraftRef.current = {
      classId: sourceClassId,
      draft: {
        ...formData,
        students: remaining,
        rumaalLevel: calculateClassRumaalRank(remaining),
      },
    };
    setMovingStudent(null);
    setMoveTargetId('');
    setIsMoving(false);
    // Refresh both classes from the server so all views stay in sync. A failure
    // here isn't a failed move — the grid just refreshes on next load.
    try {
      await dispatch(fetchClasses());
    } catch {
      /* ignore */
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this class?')) {
      const classId = selectedClass.id || selectedClass._id;
      if (classId) {
        dispatch(deleteClass(classId));
      }
      dispatch(closePanel());
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (selectedClass.isNew) {
      dispatch(closePanel());
    }
  };

  const getInstructorsForType = (type) => {
    // First try to get from API (Redux state)
    if (instructors && instructors.length > 0) {
      const apiInstructors = instructors
        .filter((i) => i.isActive !== false && i.classTypes && i.classTypes.includes(type))
        .map((i) => i.name)
        .sort();
      
      if (apiInstructors.length > 0) {
        return apiInstructors;
      }
    }
    
    // Fallback to constants.js if API instructors not available
    switch (type) {
      case 'Bhangra':
        return BHANGRA_INSTRUCTORS;
      case 'Gidha':
        return GIDHA_INSTRUCTORS;
      case 'Dhol':
        return DHOL_INSTRUCTORS;
      case 'Bollywood':
        return BOLLYWOOD_INSTRUCTORS;
      default:
        return [];
    }
  };

  const getInstructors = () => {
    return getInstructorsForType(formData.type);
  };

  const getLocations = () => {
    // First try to get from API (Redux state)
    if (locations && locations.length > 0) {
      const apiLocations = locations
        .filter((l) => l.isActive !== false)
        .map((l) => l.name)
        .sort();
      
      if (apiLocations.length > 0) {
        return apiLocations;
      }
    }
    
    // Fallback to constants.js if API locations not available
    return LOCATIONS;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 transition-opacity duration-200"
        onClick={() => dispatch(closePanel())}
      />

      {/* Panel - Minimal and clean */}
      <div className="relative w-full sm:max-w-md h-full bg-white overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-3 sm:p-4 md:p-5 z-10">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
              {isEditing ? (
                <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className="border border-gray-300 rounded-md px-2 sm:px-3 py-1 sm:py-1.5 text-sm sm:text-base font-semibold focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    {CLASS_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <span className="text-gray-400 hidden sm:inline">–</span>
                  <select
                    value={formData.ageGroup}
                    onChange={(e) => handleInputChange('ageGroup', e.target.value)}
                    className="border border-gray-300 rounded-md px-2 sm:px-3 py-1 sm:py-1.5 text-sm sm:text-base font-semibold focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    {AGE_GROUPS.map((group) => (
                      <option key={group} value={group}>
                        {group}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                `${selectedClass.type} – ${selectedClass.ageGroup}`
              )}
            </h2>
            <button
              onClick={() => dispatch(closePanel())}
              className="text-gray-400 hover:text-gray-600 text-2xl w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              ×
            </button>
          </div>

          {formData.name && (
            <p className="text-sm text-gray-600">{formData.name}</p>
          )}
        </div>

        <div className="p-3 sm:p-4 md:p-5 space-y-4 sm:space-y-5">
          {/* Class Name */}
          {isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Class Name (Optional)
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                placeholder="e.g., Advanced Bhangra"
              />
            </div>
          )}

          {/* Instructor */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Instructor
              </label>
              {isEditing && (
                <button
                  onClick={() => setShowInstructorList(!showInstructorList)}
                  className="p-1 text-gray-600 hover:text-orange-600 transition-colors"
                  aria-label="Manage Instructors"
                  title="Manage Instructors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}
            </div>
            {isEditing ? (
              <SearchableSelect
                value={formData.instructor}
                onChange={(val) => handleInputChange('instructor', val)}
                options={getInstructors()}
                placeholder="Select Instructor"
                emptyLabel="No instructor"
              />
            ) : (
              <p className="text-gray-800">{selectedClass.instructor || 'Not assigned'}</p>
            )}
            
            {/* Instructor List */}
            {showInstructorList && isEditing && (
              <div className="mt-3">
                <InstructorList isEditing={isEditing} classType={formData.type} />
              </div>
            )}
          </div>

          {/* Time & Duration */}
          {isEditing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Day
                </label>
                <select
                  value={formData.day}
                  onChange={(e) => handleInputChange('day', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                >
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(
                    (day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    )
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time
                </label>
                <select
                  value={formData.startTime}
                  onChange={(e) => handleInputChange('startTime', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                >
                  {getAllTimeOptions().map((time) => (
                    <option key={time} value={time}>
                      {formatTime(time)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-600">
                {selectedClass.day} at {formatTime(selectedClass.startTime)}
              </p>
            </div>
          )}

          {isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration
              </label>
              <select
                value={formData.duration}
                onChange={(e) => handleInputChange('duration', parseInt(e.target.value, 10))}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              >
                {DURATION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Location */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Location
              </label>
              {isEditing && (
                <button
                  onClick={() => setShowLocationList(!showLocationList)}
                  className="p-1 text-gray-600 hover:text-orange-600 transition-colors"
                  aria-label="Manage Locations"
                  title="Manage Locations"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}
            </div>
            {isEditing ? (
              <select
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              >
                <option value="">Select Location</option>
                {getLocations().map((location) => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-gray-800">{selectedClass.location || 'Not set'}</p>
            )}
            
            {/* Location List */}
            {showLocationList && isEditing && (
              <div className="mt-3">
                <LocationList isEditing={isEditing} />
              </div>
            )}
          </div>

          {/* Rumaal Level - Auto-calculated, display only */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rumaal Level
            </label>
            <div className="flex items-center gap-2">
              <div 
                className="px-3 py-2 rounded-full font-semibold text-sm shadow-sm border border-gray-200"
                style={{ 
                  backgroundColor: formData.rumaalLevel === 'White' ? '#FFFFFF' : 
                    formData.rumaalLevel === 'Yellow' ? '#FCD34D' :
                    formData.rumaalLevel === 'Green' ? '#10B981' :
                    formData.rumaalLevel === 'Blue' ? '#3B82F6' :
                    formData.rumaalLevel === 'Red' ? '#EF4444' :
                    formData.rumaalLevel === 'Black' ? '#1F2937' : '#9CA3AF',
                  color: formData.rumaalLevel === 'White' || formData.rumaalLevel === 'Yellow' ? '#1F2937' : '#FFFFFF'
                }}
              >
                {formData.rumaalLevel}
              </div>
              <span className="text-xs text-gray-500">(Auto-calculated — weighted average of student ranks)</span>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            {isEditing ? (
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 sm:px-3 sm:py-2 h-24"
                placeholder="Practice notes or reminders..."
              />
            ) : (
              <p className="text-gray-800 whitespace-pre-wrap">
                {selectedClass.notes || 'No notes'}
              </p>
            )}
          </div>

          {/* Student List */}
          <StudentList
            students={formData.students}
            isEditing={isEditing}
            onStudentsChange={handleStudentsChange}
            instructor={formData.instructor}
            onMoveStudent={handleMoveStudent}
            highlightStudent={highlightedStudent}
          />

          {/* Action Buttons - Clean and minimal */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4 sm:pt-5 border-t border-gray-200 bg-gray-50/50 -mx-3 sm:-mx-4 md:-mx-5 px-3 sm:px-4 md:px-5 pb-3 sm:pb-5">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  className="flex-1 bg-orange-500 text-white px-3 py-2 sm:px-4 sm:py-2.5 rounded-md font-semibold hover:bg-orange-600 transition-colors shadow-sm"
                >
                  Save Class
                </button>
                <button
                  onClick={handleCancel}
                  className="px-3 py-2 sm:px-4 sm:py-2.5 border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-white transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex-1 bg-orange-500 text-white px-3 py-2 sm:px-4 sm:py-2.5 rounded-md font-semibold hover:bg-orange-600 transition-colors shadow-sm"
                >
                  Edit Class
                </button>
                <button
                  onClick={handleDelete}
                  className="px-3 py-2 sm:px-4 sm:py-2.5 border border-red-300 text-red-600 rounded-md font-medium hover:bg-red-50 transition-colors"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Move student to another class */}
      {movingStudent && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={isMoving ? undefined : cancelMove}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Move Student</h3>
            <p className="text-sm text-gray-600 mb-4">
              Move <span className="font-medium">{movingStudent.name}</span> to another class.
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Destination class
            </label>
            <select
              value={moveTargetId}
              onChange={(e) => setMoveTargetId(e.target.value)}
              disabled={isMoving}
              className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent mb-3"
            >
              <option value="">Select a class…</option>
              {classes
                .filter((c) => {
                  const cid = c.id || c._id;
                  const currentId = selectedClass.id || selectedClass._id;
                  return cid !== currentId;
                })
                .map((c) => (
                  <option key={c.id || c._id} value={c.id || c._id}>
                    {getClassLabel(c)}
                  </option>
                ))}
            </select>

            {moveError && (
              <p className="text-sm text-red-600 mb-3">{moveError}</p>
            )}

            <div className="flex gap-2">
              <button
                onClick={confirmMove}
                disabled={!moveTargetId || isMoving}
                className="flex-1 bg-orange-500 text-white px-3 py-2 rounded-md font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isMoving ? 'Moving…' : 'Move'}
              </button>
              <button
                onClick={cancelMove}
                disabled={isMoving}
                className="px-3 py-2 border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassDetailsPanel;

